import { useState, useEffect } from 'react';
import { History } from 'lucide-react';
import { getTransferHistory, type TransferRecord } from '@/lib/ipdApi';
import { format } from 'date-fns';

function InfoRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-sm font-medium">{value || '—'}</p>
    </div>
  );
}

function TransferHistorySection({ admissionId, bedId }: { admissionId: string; bedId?: string }) {
  const [transfers, setTransfers] = useState<TransferRecord[]>([]);

  useEffect(() => {
    getTransferHistory(admissionId).then(data => setTransfers(data ?? [])).catch(console.error);
  }, [admissionId, bedId]);

  if (transfers.length === 0) return null;

  return (
    <div>
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3 border-b pb-1 flex items-center gap-1.5">
        <History className="w-3.5 h-3.5" /> Ward / Bed Transfer History
      </h3>
      <div className="space-y-2">
        {transfers.map(t => (
          <div key={t.id} className="text-sm flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <span className="text-xs text-muted-foreground">
              {format(new Date(t.transferred_at), 'dd MMM yyyy, h:mm a')}
            </span>
            <span>
              {t.from_ward_name} / {t.from_bed_number} → {t.to_ward_name} / {t.to_bed_number}
            </span>
            {t.notes && <span className="text-xs text-muted-foreground">— {t.notes}</span>}
            <span className="text-xs text-muted-foreground">by {t.transferred_by_name}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function IPDOverviewTab({ admission }: { admission: any }) {
  if (!admission) return null;
  return (
    <div className="space-y-6">
      {/* Additional details */}
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3 border-b pb-1">
          Additional Details
        </h3>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <InfoRow label="Blood Group"        value={admission.patient_blood_group} />
          <InfoRow label="Allergies"          value={admission.patient_allergies} />
          <InfoRow label="Discharged On"
            value={admission.discharge_date
              ? format(new Date(admission.discharge_date), 'dd MMM yyyy, h:mm a')
              : undefined}
          />
          <InfoRow label="Admitting Doctor"   value={admission.admitting_doctor_name} />
          <InfoRow label="Consulting Doctor"  value={admission.consulting_doctor_name} />
          <InfoRow label="Referred By"        value={admission.referred_by} />
          <InfoRow label="Department"         value={admission.department} />
          <InfoRow label="Rate Plan"          value={admission.rate_plan} />
          <InfoRow label="Insurance Provider" value={admission.insurance_provider} />
        </div>
      </div>

      {admission.notes && (
        <div>
          <p className="text-xs text-muted-foreground mb-1">Notes</p>
          <p className="text-sm whitespace-pre-wrap">{admission.notes}</p>
        </div>
      )}

      <TransferHistorySection admissionId={admission.id} bedId={admission.bed_id} />
    </div>
  );
}
